import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared/components";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { ConnectorHostingWithServiceAccount } from "./connector-hosting.ts";
import { INVALID_INSTANCE_URL, isInstanceUrl } from "./instance-url.ts";
import { INVALID_ITEM_ID, isItemId } from "./item-id.ts";
import { INVALID_MANAGING_APP, isManagingApp } from "./managing-app.ts";
import {
  DEFAULT_NAMESPACE,
  INVALID_NAMESPACE,
  isNamespaceOrBlank,
} from "./namespace.ts";
import { INVALID_SECRET_NAME, isSecretName } from "./secret-name.ts";

/** Two items, so that one connector can serve several Argo CD instances and
 * be reinstalled without touching them.
 *
 * `connector` is one deployment of P0's Argo CD connector: where it runs and,
 * from that, the identity it runs as. `argocd-instance` is one Argo CD
 * instance: which connector reaches it, the Application that manages its
 * `argocd-rbac-cm`, and where the connector finds the `p0-connector`
 * account's API token.
 *
 * Shaped after Harbor's components
 * (packages/integrations/harbor/shared/src/components.ts).
 */
export const ArgocdComponents = InstallSpec({
  connector: ItemComponent({
    label: "Argo CD connector",
    description:
      "Runs P0's Argo CD connector in your AWS account or Google Cloud project",
    type: "string",
    maturity: "beta",
    // A connector's request holds only `hosting`, so the install framework
    // gives nothing away when it repeats the request beside a refused id.
    validator: async (id) => (isItemId(id) ? undefined : INVALID_ITEM_ID),
    schema: {
      // The variant that names the connector's identity after the connector,
      // so each instance can grant that identity read on its secret.
      hosting: ConnectorHostingWithServiceAccount,
    },
  }),
  "argocd-instance": ItemComponent({
    label: "Argo CD instance",
    description: "An Argo CD instance P0 can grant roles on",
    type: "string",
    maturity: "beta",
    // No item validator, though an instance's id has the connector's rule
    // (`isItemId`). The install framework answers a failing item validator
    // with the whole request, and logs it, and an instance's request may hold
    // the API token pasted in place of its secret's name. So the instance's
    // assembler has to check the id itself.
    //
    // Every field is `step: "new"`, so a verify or a configure that changes
    // one is refused. A PUT isn't held to that: the instance's assembler has
    // to refuse a PUT over an existing item. An instance's read grant names
    // its connector's identity and its secret, so a new connector, vault or
    // secret takes a new item. So does a new URL, managing app or namespace:
    // an open request's grant is a line in that instance's `argocd-rbac-cm`,
    // and its revoke has to find the line where the grant wrote it.
    schema: {
      // A `connector` item's id.
      connector: Element({
        label: "Connector",
        description:
          "The Argo CD connector that reaches this instance. It must run in the same cloud as the instance's secret.",
        type: "dynamic",
        step: "new",
      }),
      url: Element({
        label: "Argo CD URL",
        description:
          "The address your Argo CD is served at, without a path. An Argo CD served under a root path isn't supported.",
        type: "string",
        placeholder: "https://argocd.example.com",
        step: "new",
        validator: async (_id, url) =>
          isInstanceUrl(url) ? undefined : INVALID_INSTANCE_URL,
      }),
      // The connector writes grants through this Application's resource API,
      // so the customer's `p0-connector` policy names it.
      managingApp: Element({
        label: "Managing application",
        description:
          "The Argo CD Application that manages argocd-rbac-cm, as <project>/<application>",
        type: "string",
        placeholder: "default/argocd",
        step: "new",
        validator: async (_id, app) =>
          isManagingApp(app) ? undefined : INVALID_MANAGING_APP,
      }),
      // Optional because nearly every install keeps Argo CD's default. Read
      // it with `toNamespace`, which fills that default in.
      namespace: Element({
        label: "Namespace",
        description: `The Kubernetes namespace argocd-rbac-cm is in. Leave it empty for ${DEFAULT_NAMESPACE}.`,
        type: "string",
        placeholder: DEFAULT_NAMESPACE,
        optional: true,
        step: "new",
        validator: async (_id, namespace) =>
          isNamespaceOrBlank(namespace) ? undefined : INVALID_NAMESPACE,
      }),
      vault: withoutAzureKeyVault(
        newVaultElement({ owner: "Argo CD instance" })
      ),
      secretName: Element({
        label: "API token secret",
        description:
          'The name of the secret that holds the p0-connector account\'s API token as {"token": "..."}, in the vault above. P0 never reads it; only your connector does.',
        type: "string",
        step: "new",
        validator: async (_id, name) =>
          isSecretName(name) ? undefined : INVALID_SECRET_NAME,
      }),
    },
  }),
});
